import { useState } from 'react';
import { Button, Skeleton, Toastify } from 'lift-ui';
import { jobsErrorMessage } from '../api';
import { useCreateSyncRuns, useJobsAccess, useSyncUsers } from '../hooks';
import type { CreateSyncRunsRequest, SyncIntent, SyncLedgerCounts, SyncMode, SyncUser, SyncUserType } from '../types';
import { formatCount } from '../utils';
import { ConfirmDialog } from './ConfirmDialog';
import { PasteEmails } from './sync-to-lift/PasteEmails';
import { PeoplePicker } from './sync-to-lift/PeoplePicker';
import { SelectionSummary } from './sync-to-lift/SelectionSummary';
import { TYPES, TYPE_LABEL, selectionByType, userKey, type Selection } from './sync-to-lift/shared';

type Scope = 'all' | SyncUserType | 'selected';

const SCOPES: { value: Scope; label: string }[] = [
  { value: 'all', label: 'Everyone' },
  { value: 'admin', label: TYPE_LABEL.admin },
  { value: 'brand_admin', label: TYPE_LABEL.brand_admin },
  { value: 'creator', label: TYPE_LABEL.creator },
  { value: 'selected', label: 'Specific people' },
];

const peopleLabel = (count: number) => `${formatCount(count)} ${count === 1 ? 'person' : 'people'}`;

const MODES: { value: SyncMode; label: string }[] = [
  { value: 'database_only', label: 'Database only' },
  { value: 'live', label: 'Live' },
];

interface SyncToLiftTabProps {
  onOpenRun: (batchId: string) => void;
}

export const SyncToLiftTab = ({ onOpenRun }: SyncToLiftTabProps) => {
  const { canEdit } = useJobsAccess();
  const [scope, setScope] = useState<Scope>('selected');
  const [mode, setMode] = useState<SyncMode>('database_only');
  const [selection, setSelection] = useState<Selection>({});
  const [pendingIntent, setPendingIntent] = useState<SyncIntent | null>(null);
  const create = useCreateSyncRuns();

  const countsQuery = useSyncUsers({ page: 1, pageSize: 1, mode });
  const counts = countsQuery.data?.counts;
  const ledger = countsQuery.data?.ledger;

  const addUsers = (users: SyncUser[]) =>
    setSelection((current) => {
      const next = { ...current };
      users.forEach((user) => {
        next[userKey(user)] = user;
      });
      return next;
    });

  const removeUsers = (users: SyncUser[]) =>
    setSelection((current) => {
      const next = { ...current };
      users.forEach((user) => {
        delete next[userKey(user)];
      });
      return next;
    });

  const grouped = selectionByType(selection);
  const selectedTotal = Object.keys(selection).length;

  // Per-type counts the run will cover, used by the run bar and the confirmation.
  const perType: Record<SyncUserType, number> = {
    admin: 0,
    brand_admin: 0,
    creator: 0,
  };
  TYPES.forEach((type) => {
    if (scope === 'selected') perType[type] = grouped[type].length;
    else if (scope === 'all') perType[type] = counts?.[type] ?? 0;
    else if (scope === type) perType[type] = counts?.[type] ?? 0;
  });
  const total = TYPES.reduce((sum, type) => sum + perType[type], 0);
  const countsKnown = scope === 'selected' || Boolean(counts);

  const request = (intent: SyncIntent): CreateSyncRunsRequest => {
    const runMode: SyncMode = intent === 'resend_invites' ? 'live' : mode;
    if (scope === 'all') return { scope: 'all', mode: runMode, intent };
    if (scope === 'selected') {
      return {
        scope: 'selected',
        mode: runMode,
        intent,
        users: Object.values(selection).map((user) => ({ type: user.type, id: user.id })),
      };
    }
    return { scope: 'type', types: [scope], mode: runMode, intent };
  };

  const ledgerTotal = (field: keyof SyncLedgerCounts) => {
    const types: SyncUserType[] = scope === 'all' || scope === 'selected' ? TYPES : [scope];
    return types.reduce((sum, type) => sum + (ledger?.[type]?.[field] ?? 0), 0);
  };

  const confirm = () => {
    if (!pendingIntent) return;
    create.mutate(request(pendingIntent), {
      onSuccess: (result) => {
        setPendingIntent(null);
        const runs = result.batches.length;
        const skipped = result.skipped.length;
        Toastify(
          'success',
          `Started ${runs} sync run${runs === 1 ? '' : 's'}${skipped ? `, ${skipped} skipped` : ''}.`
        );
        if (scope === 'selected') setSelection({});
        if (result.batches[0]) onOpenRun(result.batches[0].id);
      },
      onError: () => setPendingIntent(null),
    });
  };

  const label = countsKnown ? `Sync ${peopleLabel(total)} to LIFT` : 'Sync to LIFT';
  const disabled = !canEdit || total === 0 || !countsKnown || create.isPending;
  const showPicker = scope === 'selected';
  const retryCount = ledgerTotal('failed') + ledgerTotal('partial');
  const emailedCount = ledgerTotal('emailed');
  const typeActions = scope !== 'selected';
  const runModeLabel = mode === 'live' ? 'Live' : 'Database only';

  const confirmCopy = () => {
    const who = TYPES.filter((type) => perType[type] > 0)
      .map((type) => `${formatCount(perType[type])} ${TYPE_LABEL[type]}`)
      .join(', ');
    if (pendingIntent === 'resend_invites') {
      return `Resend invites for ${who || 'the selected type'}. Only people who were emailed before and have not signed in to LIFT are included. Each person gets one email.`;
    }
    if (pendingIntent === 'retry_failed') {
      return `Retry failed and partial ${who || 'people'}. The run checks what already persisted in LIFT and repairs only the missing steps.`;
    }
    if (mode === 'live') {
      return `${who}. Creates Firebase accounts where missing and sends one claim or reset email to people who have not been emailed.`;
    }
    return `${who}. No Firebase reads or writes. No email goes to these people. A zip report will be downloadable from Sync Runs.`;
  };

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-4 rounded-md border border-border p-5" aria-label="Scope">
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold">Sync to LIFT</h2>
          <p className="text-sm text-muted-foreground">
            Create or update LIFT accounts for Grapevine users. Choose who to include, then start the run.
          </p>
        </div>

        {countsQuery.isError && (
          <p className="text-sm text-destructive">
            {jobsErrorMessage(countsQuery.error, 'Eligible people could not be loaded.')}
          </p>
        )}

        <div className="flex flex-wrap gap-1" role="group" aria-label="Who to sync">
          {SCOPES.map((option) => {
            const count =
              option.value === 'selected'
                ? undefined
                : option.value === 'all'
                  ? counts && TYPES.reduce((sum, type) => sum + counts[type], 0)
                  : counts?.[option.value];
            return (
              <Button
                key={option.value}
                type="button"
                size="sm"
                variant={scope === option.value ? 'default' : 'outline'}
                aria-pressed={scope === option.value}
                onClick={() => setScope(option.value)}
              >
                {option.label}
                {option.value !== 'selected' &&
                  (countsQuery.isLoading ? (
                    <Skeleton className="ml-1.5 inline-block h-3 w-8 align-middle" />
                  ) : (
                    count !== undefined && <span className="ml-1.5 tabular-nums opacity-70">{formatCount(count)}</span>
                  ))}
              </Button>
            );
          })}
        </div>

        <div className="flex flex-wrap gap-1" role="group" aria-label="Sync mode">
          {MODES.map((option) => (
            <Button
              key={option.value}
              type="button"
              size="sm"
              variant={mode === option.value ? 'default' : 'outline'}
              aria-pressed={mode === option.value}
              onClick={() => setMode(option.value)}
            >
              {option.label}
            </Button>
          ))}
        </div>

        <p className="text-sm text-muted-foreground">
          Local ledger · {formatCount(ledgerTotal('complete'))} complete · {formatCount(ledgerTotal('failed'))} failed ·{' '}
          {formatCount(ledgerTotal('partial'))} partial
        </p>

        {showPicker ? (
          <div className="grid items-start gap-6 md:grid-cols-2">
            <div className="flex min-w-0 flex-col gap-4">
              <PeoplePicker
                selection={selection}
                counts={counts}
                onAdd={addUsers}
                onRemove={removeUsers}
                onClear={() => setSelection({})}
              />
              <SelectionSummary selection={selection} onRemove={removeUsers} />
            </div>
            <PasteEmails onAdd={addUsers} />
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            {scope === 'all'
              ? 'Every eligible GV admin, brand admin and creator will be included.'
              : `Every eligible person in ${TYPE_LABEL[scope as SyncUserType]} will be included.`}
          </p>
        )}
      </section>

      <div className="sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-background px-5 py-3">
        <span className="text-sm text-muted-foreground">
          {runModeLabel} · zip report available in Sync Runs
          {!canEdit && ' · You need editor access to start a sync'}
        </span>
        <div className="flex flex-wrap gap-2">
          {typeActions && (
            <>
              <Button
                type="button"
                variant="outline"
                disabled={!canEdit || retryCount === 0 || create.isPending}
                onClick={() => setPendingIntent('retry_failed')}
              >
                Retry failed
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={!canEdit || emailedCount === 0 || create.isPending}
                onClick={() => setPendingIntent('resend_invites')}
              >
                Resend invites
              </Button>
            </>
          )}
          <Button type="button" disabled={disabled} onClick={() => setPendingIntent('sync_all')}>
            {create.isPending ? 'Working…' : label}
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={pendingIntent !== null}
        title={
          pendingIntent === 'resend_invites'
            ? 'Resend invites?'
            : pendingIntent === 'retry_failed'
              ? 'Retry failed people?'
              : `Sync ${peopleLabel(total)} to LIFT?`
        }
        description={confirmCopy()}
        confirmLabel={pendingIntent === 'resend_invites' ? 'Send invites' : 'Start sync'}
        pending={create.isPending}
        onConfirm={confirm}
        onCancel={() => setPendingIntent(null)}
      />
    </div>
  );
};
